import React, { useMemo } from 'react';
import { MultiPageVideoModel, Shot, Template, TemplateElement } from '@bynder-studio/render-core';
import { List, InlineBanner } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { flattenTree } from 'packages/common/editor/helpers/elementtree';
import { convertFrameToTime } from 'packages/common/editor/timeline/helpers/frame-to-time-converter';
import {
    getElementIdsForSceneRange,
    getGlobalEditableElements,
    getSceneElementStateMap,
    getShotDisabledElementIds,
    getVideoDesignSceneRows,
} from './utils';
import { useReviewStatus } from './useReviewStatus';
import ShotItemGlobal from './ShotItemGlobal';
import ShotReviewBanner from './ShotReviewBanner';
import useVariations from 'packages/pages/design/hooks/useVariations';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';
import SceneRows from './SceneRows';
import { TranscriptListItem } from 'packages/pages/components/Transcript';
import { DesignTab } from 'packages/pages/design/sidebar/types';

type SceneRangeElement = {
    id: number | string;
    startFrame?: number;
    children?: SceneRangeElement[];
    properties?: Record<string, unknown>;
    toObject?: () => Record<string, unknown>;
};

type SceneRangeTemplateElement = {
    id: number;
    properties: {
        startFrame: number;
    } & Record<string, unknown>;
    children: SceneRangeTemplateElement[];
} & Record<string, unknown>;

const normalizeSceneRangeElement = (element: SceneRangeElement): SceneRangeTemplateElement => {
    const serializedElement = (element.toObject?.() ?? element) as SceneRangeElement;
    const { children, properties, ...rest } = serializedElement as SceneRangeElement & Record<string, unknown>;

    return {
        ...rest,
        id: Number(serializedElement.id),
        properties: {
            ...(properties || {}),
            startFrame: Number(serializedElement.startFrame ?? properties?.startFrame ?? 0),
        },
        children: Array.isArray(children) ? children.map(normalizeSceneRangeElement) : [],
    };
};

type Props = {
    template: Template;
    currentPageIndex: number;
    elements: TemplateElement[];
    shots: Shot[];
    selectedShot: Shot;
    selectedShotIndex: number;
    isToolbarDisabled: boolean;
    isLoading: boolean;
    setDesignTab: (tab: DesignTab) => void;
};

const ShotVideoDesign = ({
    template,
    currentPageIndex,
    elements,
    shots,
    selectedShot,
    selectedShotIndex,
    setDesignTab,
    isToolbarDisabled,
    isLoading,
}: Props) => {
    const { translate } = useTranslate();
    const { isDisabledByReview } = useReviewStatus();
    const { deletedAssets } = useVariations();
    const { creativeModel } = useDesign<MultiPageVideoModel>();
    const { isPlatformAllowed } = useAccessRights();

    const { frameRate } = template.pages[currentPageIndex];
    const currentModel = creativeModel.getModels()[creativeModel.getCurrentPageIndex()];
    const modelElements = currentModel?.getAllElementsRecursively?.() || [];
    const modelSceneElements = currentModel?.getElements?.().map(normalizeSceneRangeElement) || [];
    const previousShot = shots[selectedShotIndex - 1];

    const isDynamicElementSelectionEnabled = useMemo(
        () => isPlatformAllowed([features.DYNAMIC_ELEMENT_SELECTION]),
        [isPlatformAllowed],
    );

    const elementStatesById = getSceneElementStateMap(modelElements);

    const allowedIds = selectedShot
        ? getElementIdsForSceneRange(modelSceneElements as unknown as TemplateElement[], {
              fromFrame: selectedShot.startFrame,
              toFrame: selectedShot.startFrame + selectedShot.duration - 1,
          })
        : new Set<number>();

    const disabledIds = getShotDisabledElementIds({
        elements,
        currentShot: selectedShot,
        previousShot,
    });

    const sceneRows = getVideoDesignSceneRows({
        elements,
        elementStatesById,
        isDynamicElementSelectionEnabled,
        allowedIds,
        disabledIds,
    });

    const globalEditableElements = useMemo(
        () => getGlobalEditableElements(flattenTree(elements) as TemplateElement),
        [elements],
    );

    return (
        <List>
            <List.Item
                subtext={
                    <>
                        {convertFrameToTime(selectedShot?.startFrame || 0, frameRate)} -{' '}
                        {convertFrameToTime(selectedShot?.startFrame + selectedShot?.duration, frameRate)}
                    </>
                }
            >
                {translate('design.sidebar.scene.globals.duration.label', {
                    name: selectedShot ? selectedShot.name : '',
                })}
            </List.Item>
            <ShotReviewBanner />
            {!!deletedAssets.length && (
                <InlineBanner variant="error">{translate('design.sidebar.scene.replace-missing-asset')}</InlineBanner>
            )}
            {isPlatformAllowed([features.TRANSCRIPT]) && creativeModel.getTranscriptElement()?.enabled && (
                <TranscriptListItem
                    creativeModel={creativeModel}
                    onClick={() => {
                        setDesignTab(DesignTab.TRANSCRIPT);
                    }}
                    context="content-configurator"
                />
            )}
            {globalEditableElements.map((element, idx) => (
                <ShotItemGlobal
                    key={element.id}
                    id={element.id}
                    isDisabled={isDisabledByReview || isToolbarDisabled}
                    isLast={idx === globalEditableElements.length - 1}
                />
            ))}
            <SceneRows
                rows={sceneRows}
                isToolbarDisabled={isToolbarDisabled}
                isLoading={isLoading}
                isDisabledByReview={isDisabledByReview}
            />
        </List>
    );
};

export default ShotVideoDesign;
